import React from "react";
import Footer from "./Footer";
import Review from "./Reviews";
import ZeronimoJourney from "./components/ZeronimoJourney";
import { motion, AnimatePresence } from "framer-motion";
import PhotoCarousel from "./components/PhotoCarousel";
import SEO from "./SEO";
import { trackCheckoutIntent, trackEvent, trackOutboundLink, trackProductSelection } from "./analytics";

const shopifyCartUrl = (variantId) => `https://oceanbeachbrands.myshopify.com/cart/${variantId}:1`;
const shopifyProductUrl = (handle) => `https://oceanbeachbrands.myshopify.com/products/${handle}`;

const roseReserve = {
  id: "burgenland-rose",
  name: "Zeronimo Rosé",
  price: "$35.40",
  href: shopifyCartUrl("50093638484205"),
};

const centuryBlendTwo = {
  id: "century-blend-2",
  name: "Zeronimo Century Blend 2.0",
  price: "$100.00",
  href: shopifyProductUrl("century-blend-20yr"),
};

const products = [
  {
    id: 1,
    name: 'Zeronimo Leonis Blend',
    price: '$72.00',
    rating: 3.9,
    justArrived: false,
    href: shopifyProductUrl("leonis-blend"),
    description:
      'A rich, old-world red with notes of dark cherry, forest floor, and oak spice, crafted from a 98-point-rated Austrian blend aged in French oak.',
    imageSrc: require('./pictures/leonis-blend-optimized.png'),
    imageAlt: 'Zeronimo Leonis Blend - 98-point rated Austrian blend',
    colors: [
      { name: 'Washed Black', bgColor: 'bg-gray-700', selectedColor: 'ring-gray-700' },
      { name: 'White', bgColor: 'bg-white', selectedColor: 'ring-gray-400' },
      { name: 'Washed Gray', bgColor: 'bg-gray-500', selectedColor: 'ring-gray-500' },
    ],
  },
  {
    id: 2,
    name: 'Zeronimo Zweigelt',
    price: '$35.40',
    rating: 3.9,
    justArrived: false,
    href: shopifyProductUrl("zweigelt-red"),
    description:
      'A smooth, full-bodied red with flavors of blackberry, cherry, vanilla, and hints of exotic wood spices.',
    imageSrc: require('./pictures/zweigelt-optimized.png'),
    imageAlt: 'Zeronimo Zweigelt - Full-bodied red wine',
    colors: [
      { name: 'Washed Black', bgColor: 'bg-gray-700', selectedColor: 'ring-gray-700' },
      { name: 'White', bgColor: 'bg-white', selectedColor: 'ring-gray-400' },
      { name: 'Washed Gray', bgColor: 'bg-gray-500', selectedColor: 'ring-gray-500' },
    ],
  },
  {
    id: 3,
    name: 'Zeronimo Grüner Veltliner',
    price: '$35.40',
    rating: 3.9,
    justArrived: false,
    href: shopifyCartUrl("47082231496941"),
    description:
      'A crisp white wine with green apple, peach, and lemon notes, balanced by vibrant minerality.',
    imageSrc: require('./pictures/gruner-optimized.png'),
    imageAlt: 'Zeronimo Grüner Veltliner - Crisp white wine',
    colors: [
      { name: 'Washed Black', bgColor: 'bg-gray-700', selectedColor: 'ring-gray-700' },
      { name: 'White', bgColor: 'bg-white', selectedColor: 'ring-gray-400' },
      { name: 'Washed Gray', bgColor: 'bg-gray-500', selectedColor: 'ring-gray-500' },
    ],
  },
  {
    id: 4,
    name: 'Zeronimo Sparkling Select',
    price: '$41.00',
    rating: 3.9,
    justArrived: false,
    href: shopifyProductUrl("sparkling-select"),
    description:
      'The ultimate celebration wine. A bone dry, sparkling select with charming floral aromas, roasted almonds, exotic yellow fruits meet desirable notes of brioche and honeysuckle, elevating the path to a rich, mineral finish.',
    imageSrc: require('./pictures/sparkling-select-optimized.png'),
    imageAlt: 'Zeronimo Sparkling Select - Elegant sparkling wine',
    colors: [
      { name: 'Washed Black', bgColor: 'bg-gray-700', selectedColor: 'ring-gray-700' },
      { name: 'White', bgColor: 'bg-white', selectedColor: 'ring-gray-400' },
      { name: 'Washed Gray', bgColor: 'bg-gray-500', selectedColor: 'ring-gray-500' },
    ],
  }
  // More items...
]

export default function LandingPage() {
  React.useEffect(() => {
    const scrollToHashTarget = () => {
      const hash = window.location.hash;

      if (!hash) {
        return;
      }

      const target = document.querySelector(hash);

      if (target) {
        target.scrollIntoView({ block: "start" });
      }
    };

    const timeoutId = window.setTimeout(scrollToHashTarget, 100);
    window.addEventListener("hashchange", scrollToHashTarget);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("hashchange", scrollToHashTarget);
    };
  }, []);

  const trackProductCta = (product, label) => {
    trackProductSelection(product);
    trackOutboundLink(product.href, label, {
      ecommerce_action: product.href.includes("/cart/") ? "direct_cart" : "product_detail",
    });

    if (product.href.includes("/cart/")) {
      trackEvent("add_to_cart", {
        currency: "USD",
        value: Number(String(product.price || "").replace(/[^0-9.]/g, "")) || undefined,
        items: [
          {
            item_id: String(product.id || product.name),
            item_name: product.name,
            item_brand: "Zeronimo",
            item_category: "Non-alcoholic wine",
          },
        ],
      });
      trackCheckoutIntent({ href: product.href, label, product });
    }
  };

  return (
    <div className="bg-black min-h-screen font-sans text-white relative">
      <SEO
        title="Ocean Beach Brands | Zeronimo Premium Non-Alcoholic Wine"
        description="Shop Zeronimo premium non-alcoholic wines from Ocean Beach Brands, including Burgenland Rosé, Century Blend, Leonis Blend, Zweigelt, Grüner Veltliner, and Sparkling Select."
        canonical="https://oceanbeachbrands.com/"
      />
      {/* Symmetrical Dual-Hero New Arrivals Section */}
      <div className="relative pt-24 pb-20 px-4 sm:px-6 lg:pt-28 lg:pb-32 lg:px-8 bg-black border-b border-gray-900/60 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_28%_42%,rgba(40,70,105,0.22),rgba(0,0,0,0)_36%),radial-gradient(circle_at_70%_36%,rgba(180,88,100,0.16),rgba(0,0,0,0)_32%)]" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black via-black/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full">
          <div className="text-center mb-16">
            <span className="text-[#9eb6c3] font-black tracking-[0.25em] uppercase text-xs block mb-3">Now Shipping</span>
            <h1 className="text-4xl sm:text-6xl font-sans-luxury font-black text-gray-200 leading-tight tracking-tighter">
              Burgenland Rosé
            </h1>
            <p className="text-lg text-gray-400 mt-4 max-w-2xl mx-auto font-medium">
              A brand new Zeronimo release from Burgenland, now shipping alongside Century Blend No. 2.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Hero Card 1: Zeronimo Rosé */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-white/5 bg-[#040506]/70 p-8 rounded-sm hover:border-rose-300/20 transition-all duration-1000 relative overflow-hidden group">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_34%_42%,rgba(31,63,96,0.26),transparent_36%),radial-gradient(circle_at_38%_58%,rgba(210,124,130,0.14),transparent_32%)] opacity-90" />
              <div className="md:col-span-5 flex justify-center h-[350px] sm:h-[450px] relative z-10">
                <img
                  src={require("./pictures/rose-packshot-optimized.webp")}
                  alt="Zeronimo Rosé"
                  className="h-full object-contain drop-shadow-[0_15px_30px_rgba(0,0,0,0.8)] brightness-110 group-hover:scale-[1.01] transition-transform duration-[1.5s] ease-[0.25,0.1,0.25,1.0]"
                  fetchpriority="high"
                />
              </div>
              <div className="md:col-span-7 flex flex-col justify-center text-center md:text-left relative z-10">
                <span className="text-[#9eb6c3] font-black tracking-[0.25em] uppercase text-[10px] mb-2 block">New Release // Now Shipping</span>
                <h2 className="text-3xl sm:text-4xl font-sans-luxury font-black text-gray-200 leading-tight mb-2 tracking-tighter">
                  Burgenland Rosé
                </h2>
                <p className="text-sm font-black text-[#9eb6c3] font-mono tracking-widest mb-4">NOW SHIPPING // {roseReserve.price}</p>
                <p className="text-sm text-gray-400 mb-6 leading-relaxed font-medium">
                  Strawberry, fresh mint, orange zest, and cool minerality meet stone fruit, rhubarb, and a lively citrus finish.
                </p>
                <a
                  href={roseReserve.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackProductCta(roseReserve, "Shop Rosé Now Shipping")}
                  className="w-full md:w-auto px-6 py-4 bg-transparent border border-[#8fa8b6]/45 hover:border-[#bfd0d9] text-[#d7e3e8] text-sm font-black tracking-widest uppercase transition-all duration-500 rounded-sm shadow-[0_0_30px_rgba(143,168,182,0.08)]"
                >
                  Shop Rosé
                </a>
              </div>
            </div>

            {/* Hero Card 2: Zeronimo Century Blend 2.0 */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-white/5 bg-[#040506]/70 p-8 rounded-sm hover:border-yellow-500/10 transition-all duration-1000 relative overflow-hidden group">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_35%_45%,rgba(31,63,96,0.20),transparent_36%)] opacity-90" />
              <div className="md:col-span-5 flex justify-center h-[350px] sm:h-[450px] relative z-10">
                <img
                  src={require("./pictures/century-blend-2-packshot-optimized.webp")}
                  alt="Zeronimo Century Blend 2.0"
                  className="h-full object-contain drop-shadow-[0_15px_30px_rgba(0,0,0,0.8)] brightness-110 group-hover:scale-[1.01] transition-transform duration-[1.5s] ease-[0.25,0.1,0.25,1.0]"
                  fetchpriority="high"
                />
              </div>
              <div className="md:col-span-7 flex flex-col justify-center text-center md:text-left relative z-10">
                <span className="text-[#9eb6c3] font-black tracking-[0.25em] uppercase text-[10px] mb-2 block">Limited Release // Now Shipping</span>
                <h2 className="text-3xl sm:text-4xl font-sans-luxury font-black text-gray-200 leading-tight mb-2 tracking-tighter">
                  Century Blend No. 2
                </h2>
                <p className="text-sm font-black text-yellow-500 font-mono tracking-widest mb-4">NOW SHIPPING // {centuryBlendTwo.price}</p>
                <p className="text-sm text-gray-400 mb-6 leading-relaxed font-medium">
                  Century Blend No. 2. The second iteration of our multi-decade dealcoholization masterwork. Extremely limited release of 2,500 numbered bottles.
                </p>
                <a
                  href={centuryBlendTwo.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackProductCta(centuryBlendTwo, "Shop Century Blend No. 2 Now Shipping")}
                  className="w-full md:w-auto px-6 py-4 bg-transparent border border-yellow-500/30 hover:border-yellow-500 text-yellow-500 text-sm font-black tracking-widest uppercase transition-all duration-500 rounded-sm"
                >
                  Shop Century Blend No. 2
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>



      <div className="overflow-hidden bg-black shadow sm:rounded-md">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <ul className="border-t border-gray-900 mt-8 grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 divide-gray-900">
            {products.map((product, index) => (
              <AnimatePresence key={product.id}>
                <motion.li
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="px-4 py-12 sm:py-24 sm:px-6 hover:bg-white/[0.02] transition-all duration-700 border-b border-gray-900 group"
                >
                  <div className="flex flex-col w-full items-center gap-y-8 sm:gap-y-10 max-w-2xl mx-auto">
                    <div className="flex justify-center px-4 sm:px-8">
                      <div className="relative">
                        <div className="absolute inset-0 bg-blue-600/5 rounded-full blur-[80px] sm:blur-[100px] group-hover:bg-blue-600/10 transition-all duration-1000" />
                        <motion.img
                          whileHover={{ scale: 1.01, y: -4 }}
                          alt={product.imageAlt}
                          src={product.imageSrc}
                          className="h-[450px] sm:h-[550px] lg:h-[600px] object-contain relative z-10 drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)] sm:drop-shadow-[0_30px_50px_rgba(0,0,0,0.5)] transition-all duration-700 brightness-110 contrast-110"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col justify-center text-center px-4">
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <h2 className="text-2xl lg:text-3xl font-black text-gray-200 tracking-tighter uppercase leading-none">{product.name}</h2>
                      </div>
                      <div className="flex gap-2 mb-6 justify-center">
                        <span className="inline-flex items-center rounded-sm bg-[#8fa8b6]/[0.08] px-2 py-0.5 text-[10px] font-black tracking-widest text-[#bfd0d9] border border-[#8fa8b6]/25 uppercase">
                          NOW SHIPPING
                        </span>
                      </div>

                      <section aria-labelledby={`information-heading-${product.id}`} className="mt-3">
                        <h3 id={`information-heading-${product.id}`} className="sr-only">
                          Product information
                        </h3>

                        <div className="mt-4 sm:mt-6">
                          <h4 className="sr-only">Description</h4>
                          <p className="text-base sm:text-lg text-gray-400 didact-gothic-regular leading-relaxed max-w-md mx-auto">{product.description}</p>
                        </div>
                      </section>

                      <section aria-labelledby={`options-heading-${product.id}`} className="mt-6 sm:mt-8">
                        <h3 id={`options-heading-${product.id}`} className="sr-only">
                          Product options
                        </h3>

                        <div>
                          <div className="mt-4 sm:mt-6 w-full flex justify-center">
                            <motion.a
                              href={product.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackProductCta(product, "Shop Now")}
                              whileHover={{
                                scale: 1.01,
                                backgroundColor: "rgb(234 179 8)",
                                color: "black",
                                boxShadow: "0 15px 50px rgba(234,179,8,0.3)"
                              }}
                              whileTap={{ scale: 0.99 }}
                              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }}
                              className="flex flex-col items-center justify-center rounded-sm border-2 px-8 sm:px-10 py-3 text-sm sm:text-base font-black tracking-widest uppercase transition-all duration-500 border-yellow-500 bg-transparent text-yellow-500"
                              aria-label={`Shop ${product.name}`}
                            >
                              Shop Now
                            </motion.a>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                </motion.li>
              </AnimatePresence>
            ))}
          </ul>
        </div>
      </div>

      {/* The Journey Section */}
      <ZeronimoJourney />

      {/* <div className="md:hidden flex flex-col justify-center items-center">
          <img
            className="w-40 mb-4"
            src={require("./pictures/zeronimo_logo.png")}
            alt="Zeronimo Logo"
          />
          <p className="text-white text-center text-cursive didact-gothic-regular text-xl mx-5">
          In partnership with Ocean Beach Brands, Heribert Bayer proudly announces the US debut of ZERONIMO wine, the world’s first-ever dealcoholized 98 point rated wine.
          </p>
          <br />
          <Link to="https://zeronimowine.com">
            <p className="text-white hover:text-blue-200 text-center pb-8 didact-gothic-regular">
              Learn more about Zeronimo{" "}
            </p>
          </Link>
          <Link to="https://oceanbeachbrands.myshopify.com">
            <button className="animate-pulse bg-white text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow">
              Order now!
            </button>
          </Link>
          <button className="underline text-white hover:text-blue-200 text-center pt-8 didact-gothic-regular"
          onClick={() => window.location = "mailto:hi@oceanbeachbrands.com"}>
            Inquire about Wholesale here
            </button>
        </div> */}
      {/* <div className="grid grid-cols-2 md:grid-cols-3 px-10 md:max-w-5xl mx-auto">
        <img
          className="w-60"
          src={require("./pictures/Big Zeronimo Red.png")}
          alt="Big Zeronimo Red"
        />
        <img
          className="w-60"
          src={require("./pictures/Euro Sparkling Zeronimo Label.png")}
          alt="Euro Sparkling Zeronimo Label"
        />
        <div className="hidden md:flex md:flex-col md:justify-center md:items-center">
          <img
            className="w-40 mb-4"
            src={require("./pictures/zeronimo_logo.png")}
            alt="Zeronimo Logo"
          />
          <p className="text-white text-center text-cursive didact-gothic-regular text-xl">
          In partnership with Ocean Beach Brands, Austrian winemaker Heribert Bayer proudly announces the US debut of ZERONIMO wine, the first-ever 98 point rated wine to be dealcoholized.
          </p>
          <br />
          <Link to="https://zeronimowine.com">
            <p className="text-white hover:text-blue-200 text-center pb-10 didact-gothic-regular">
              Learn more about Zeronimo{" "}
            </p>
          </Link>
          <Link to="https://oceanbeachbrands.myshopify.com">
            <button className="animate-pulse bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow">
              Order now!
            </button>
          </Link>
          <button className="underline text-white hover:text-blue-200 text-center pt-10 didact-gothic-regular"
          onClick={() => window.location = "mailto:hi@oceanbeachbrands.com"}>
            Inquire about Wholesale here
            </button>
        </div>
      </div> */}
      <Review />
      <PhotoCarousel />
      <Footer />
    </div>
  );
}
